import {
	createContext,
	type ReactNode,
	use,
	useSyncExternalStore,
} from "react";

export interface AppHeaderCenterSnapshot {
	content: ReactNode;
	isRegistered: boolean;
}

interface AppHeaderCenterEntry {
	content: ReactNode;
	order: number;
}

export interface AppHeaderSlotsStore {
	getCenterSnapshot: () => AppHeaderCenterSnapshot;
	registerCenter: (owner: symbol, content: ReactNode) => () => void;
	subscribeCenter: (listener: () => void) => () => void;
}

const EMPTY_CENTER_SNAPSHOT: AppHeaderCenterSnapshot = {
	content: null,
	isRegistered: false,
};

export function createAppHeaderSlotsStore(): AppHeaderSlotsStore {
	const centerEntries = new Map<symbol, AppHeaderCenterEntry>();
	const centerListeners = new Set<() => void>();
	let nextOrder = 0;
	let centerSnapshot = EMPTY_CENTER_SNAPSHOT;

	const publishCenter = () => {
		let latest: AppHeaderCenterEntry | null = null;
		for (const entry of centerEntries.values()) {
			if (!latest || entry.order > latest.order) {
				latest = entry;
			}
		}
		centerSnapshot = latest
			? { content: latest.content, isRegistered: true }
			: EMPTY_CENTER_SNAPSHOT;
		for (const listener of centerListeners) {
			listener();
		}
	};

	return {
		getCenterSnapshot: () => centerSnapshot,
		registerCenter: (owner, content) => {
			nextOrder += 1;
			centerEntries.set(owner, { content, order: nextOrder });
			publishCenter();
			return () => {
				centerEntries.delete(owner);
				publishCenter();
			};
		},
		subscribeCenter: (listener) => {
			centerListeners.add(listener);
			return () => centerListeners.delete(listener);
		},
	};
}

export const AppHeaderSlotsContext = createContext<AppHeaderSlotsStore | null>(
	null,
);

/** Stands in where no provider is mounted — see `useAppHeaderCenterSlot`. */
const EMPTY_SLOTS_STORE: AppHeaderSlotsStore = {
	getCenterSnapshot: () => EMPTY_CENTER_SNAPSHOT,
	registerCenter: () => () => {
		// no provider to register into
	},
	subscribeCenter: () => () => {
		// nothing can change, so nothing to unsubscribe
	},
};

/**
 * The store a page writes its center slot into, or a no-op stand-in when no
 * provider is mounted.
 *
 * This used to throw. The router's outer `Suspense` renders
 * `CurrentRouteSkeleton` OUTSIDE `AppProviders` (see `router-components.tsx`),
 * and that skeleton declares a page-owned center slot for `/config`,
 * `/account`, `/playground` and `/superuser` — so a cold load or refresh of any
 * of them threw before the provider existed and the whole page went to
 * "Application error". Writing into a store nobody reads is harmless; crashing
 * the page is not. Mirrors the reader-side fallback in
 * {@link useAppHeaderCenterSlot}.
 */
export function useAppHeaderSlotsStore(): AppHeaderSlotsStore {
	return use(AppHeaderSlotsContext) ?? EMPTY_SLOTS_STORE;
}

/**
 * Reads the page-declared center slot.
 *
 * Returns the empty snapshot when there is NO provider above, rather than
 * throwing like `useAppHeaderSlotsStore`. The navbar reads this slot and the
 * navbar renders from `AppChrome`, which both runtimes mount — but only the app
 * shell mounts `AppHeaderSlotsProvider`. The dashboard runtime deliberately
 * skips that provider stack, so on `/dashboard` the throw took the whole page
 * down.
 *
 * A missing provider is not an error here: no provider means no page can have
 * registered anything, which is exactly what the empty snapshot says. A page
 * that DOES want the slot renders inside the shell, where the provider exists.
 */
export function useAppHeaderCenterSlot(): AppHeaderCenterSnapshot {
	// Module-scope fallback, so its identity is stable across renders and
	// `useSyncExternalStore` never resubscribes. An early return would have been
	// a conditional hook call.
	const store = use(AppHeaderSlotsContext) ?? EMPTY_SLOTS_STORE;
	return useSyncExternalStore(
		store.subscribeCenter,
		store.getCenterSnapshot,
		store.getCenterSnapshot,
	);
}
